import { useState, useMemo, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useLocation } from "react-router-dom";
import userStore from "../../zustand/userStore";
import {
  GetPaymentPlanDetailsThunk,
  InitiatePaymentThunk,
} from "./redux/thunks";
import { logout } from "../../auth/authSlice";
import Footer from "../../components/Footer";
import SkeletonLoader from "../QuestionPaper/createpaper/components/loader/skeletonLoader";
import "./pricingpage.css";
import { setAppdAiGuest, setAppdAiUser } from "../../utils/appdAiHelpers";


// School plan modules display (Home.js-style cards)
const schoolModulesList = [
  { name: "Create Question Paper", subName: "प्रश्न पत्र बनाएं", color: "#da5252", icon: "/images/questionpaper.png", dynamic: true, badgeVariant: "papers" },
  { name: "Create Academic Calendar", subName: "अकादमिक कैलेंडर बनाएं", color: "#d80564", icon: "/images/3.png", badge: "UNLIMITED", badgeVariant: "unlimited" },
  { name: "Create Time Table", subName: "समय सारिणी बनाएं", color: "#1c498d", icon: "/images/timetable.png", badge: "UNLIMITED", badgeVariant: "unlimited" },
  { name: "Create Event Organiser", subName: "इवेंट ऑर्गेनाइजर बनाएं", color: "#86bc28", icon: "/images/event.png", badge: "UNLIMITED", badgeVariant: "unlimited" },
  { name: "Create Teachers Planner", subName: "टीचर्स प्लानर बनाएं", color: "#d4166b", icon: "/images/teacherplanner.png", badge: "UNLIMITED", badgeVariant: "unlimited" },
  { name: "Documents & Correspondences", subName: "दस्तावेज़ और पत्राचार", color: "#018bd6", icon: "/images/documents.png", badge: "FREE", badgeVariant: "free" },
  { name: "Create Quiz", subName: "क्विज़ बनाएं", color: "#f89406", icon: "/images/quizicon.png", badge: "COMING SOON", badgeVariant: "soon" },
  { name: "Create Pedagogy", subName: "शिक्षाशास्त्र बनाएं", color: "#16a2b8", icon: "/images/pedagogy.png", badge: "COMING SOON", badgeVariant: "soon" },
  { name: "Create STEM", subName: "स्टेम बनाएं", color: "#27ae60", icon: "/images/stem.png", badge: "COMING SOON", badgeVariant: "soon" },
  { name: "Create User Management", subName: "उपयोगकर्ता प्रबंधन बनाएं", color: "#9b1616", icon: "/images/adduser.png", badge: "UNLIMITED", badgeVariant: "unlimited" },
];

// Question paper count per user plan size
const qpCountByUsers = { 10: 400, 25: 1000, 40: 1600, 60: 2400 };

// Available modules for individual plan selection (landing page card style)
const allModules = [
  { id: 2, name: "Create Question Papers", subName: "प्रश्न पत्र बनाएं", gradient: "linear-gradient(135deg, #C34956 0%, #E56B77 100%)", icon: "/images/questionpaper.png", badge: "50 PAPERS", badgeVariant: "papers" },
  { id: 3, name: "Create Timetable", subName: "समय सारिणी बनाएं", gradient: "linear-gradient(135deg, #16CCA3 0%, #30d3ad 100%)", icon: "/images/timetable.png", badge: "UNLIMITED", badgeVariant: "unlimited" },
  { id: 4, name: "Create Academic Calendar", subName: "अकादमिक कैलेंडर बनाएं", gradient: "linear-gradient(135deg, #d80564 0%, #f53587 100%)", icon: "/images/3.png", badge: "UNLIMITED", badgeVariant: "unlimited" },
  { id: 1, name: "Create Event Organiser", subName: "इवेंट ऑर्गेनाइजर बनाएं", gradient: "linear-gradient(135deg, #86bc28 0%, #a2d93a 100%)", icon: "/images/event.png", badge: "UNLIMITED", badgeVariant: "unlimited" },
  // { id: 5, name: "Teachers Planner", subName: "टीचर्स प्लानर", gradient: "linear-gradient(135deg, #d4166b 0%, #f03090 100%)", icon: "/images/teacherplanner.png", badge: "UNLIMITED", badgeVariant: "unlimited", requiresModule: 3 },
  { id: 6, name: "Documents & Correspondences", subName: "दस्तावेज़ और पत्राचार", gradient: "linear-gradient(135deg, #018bd6 0%, #33aaee 100%)", icon: "/images/documents.png", badge: "Free with every feature", badgeVariant: "free", locked: true },
];

function PricingPage() {
  const location = useLocation();
  const { pathname } = location;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((store) => store.authSlice);
  const userData = userStore((state) => state.user);

  // Get plan details from Redux (fetched from API)
  const { loader, error, planDetails } = useSelector(
    (store) => store.paymentGateway,
  );

  useEffect(() => {
    if (userData) setAppdAiUser(userData, "pricing");
    else setAppdAiGuest("pricing");
  }, []);
  // Fetch plans from API on mount
  useEffect(() => {
    dispatch(GetPaymentPlanDetailsThunk());
  }, [dispatch]);

  // Separate complete and customized plans from API
  const completePlans =
    planDetails?.filter((p) => p.model_type === "complete") || [];
  const customizedPlans =
    planDetails?.filter((p) => p.model_type === "customized") || [];

  // Complete Plan State
  const [selectedCompletePlan, setSelectedCompletePlan] = useState(null);
  const [completeCouponCode, setCompleteCouponCode] = useState("");
  const [completeAppliedCoupon, setCompleteAppliedCoupon] = useState(null);
  const [completeCouponId, setCompleteCouponId] = useState(null);

  // Customized Plan State
  // Documents (id:6) always pre-selected
  const [selectedModules, setSelectedModules] = useState([6]);
  const [numberOfUsers, setNumberOfUsers] = useState(1);
  const [customCouponCode, setCustomCouponCode] = useState("");
  const [customAppliedCoupon, setCustomAppliedCoupon] = useState(null);
  const [customCouponId, setCustomCouponId] = useState(null);

  const [isValidatingCoupon, setIsValidatingCoupon] = useState(false);
  const [mobileTab, setMobileTab] = useState("individual");

  // Auto-fill coupon code if passed from paywall (only within 24 hours of registration)
  useEffect(() => {
    const couponCode = location.state?.couponCode;
    if (couponCode && userData?.created) {
      const createdAt = new Date(userData.created);
      const now = new Date();
      const hoursSinceCreation = (now - createdAt) / (1000 * 60 * 60);
      if (hoursSinceCreation <= 24) {
        setCompleteCouponCode(couponCode);
        setCustomCouponCode(couponCode);
      }
    }
  }, [location.state, userData]);

  const logOut = () => {
    dispatch(logout());
    navigate("/home");
  };

  // Auto-determine plan from number of selected non-document modules
  // documents+1=id9, +2=id10, +3=id11, +4=id12, +5=id13
  const selectedCustomPlan = useMemo(() => {
    const nonDocCount = selectedModules.filter((id) => id !== 6).length;
    if (nonDocCount === 0) return null;
    const targetModelId = 8 + nonDocCount;
    return customizedPlans.find((p) => p.model_id === targetModelId) || null;
  }, [selectedModules, customizedPlans]);

  // Tier pricing: base = ₹250 × modules, final after discount
  const CUSTOM_TIER = {
    1: { base: 250,  discountPct: 0,  final: 250 },
    2: { base: 500,  discountPct: 10, final: 450 },
    3: { base: 750,  discountPct: 15, final: 635 },
    4: { base: 1000, discountPct: 20, final: 800 },
  };

  const customTier = useMemo(() => {
    const nonDocCount = selectedModules.filter((id) => id !== 6).length;
    return CUSTOM_TIER[nonDocCount] || null;
  }, [selectedModules]);

  // Base price per user (before discount), for breakdown display
  const customizedBasePricePerUser = customTier?.base ?? 0;
  // Discounted price per user
  const customizedPricePerUser = customTier?.final ?? 0;
  // Total after tier discount × users
  const customizedPrice = customizedPricePerUser * numberOfUsers;
  // "After discount" value used for coupon base
  const customizedPriceAfterDiscount = customizedPrice;

  // Calculate final price with coupon for complete plan (no base discount)
  const completePlanFinalPrice = useMemo(() => {
    if (!selectedCompletePlan) return 0;
    const basePrice = Number(selectedCompletePlan.price);
    if (!completeAppliedCoupon) return basePrice;
    const { discount } = completeAppliedCoupon;
    if (typeof discount === "string" && discount.includes("%")) {
      const percentage = parseFloat(discount.replace("%", ""));
      return Math.round(basePrice - (basePrice * percentage) / 100);
    }
    return Math.max(0, basePrice - parseFloat(discount));
  }, [selectedCompletePlan, completeAppliedCoupon]);

  // Final price after coupon (tier discount already baked in)
  const customizedFinalPrice = useMemo(() => {
    if (!customAppliedCoupon) return customizedPriceAfterDiscount;
    const { discount } = customAppliedCoupon;
    if (typeof discount === "string" && discount.includes("%")) {
      const percentage = parseFloat(discount.replace("%", ""));
      return Math.round(customizedPriceAfterDiscount - (customizedPriceAfterDiscount * percentage) / 100);
    }
    return Math.max(0, customizedPriceAfterDiscount - parseFloat(discount));
  }, [customizedPriceAfterDiscount, customAppliedCoupon]);

  // Valid when at least 1 non-document module is selected
  const isCustomizedValid = useMemo(() => {
    return selectedCustomPlan !== null && numberOfUsers >= 1;
  }, [selectedCustomPlan, numberOfUsers]);

  // Handle module card toggle
  const handleModuleToggle = (moduleId) => {
    if (moduleId === 6) return; // Documents always locked-on

    if (selectedModules.includes(moduleId)) {
      let newSelection = selectedModules.filter((id) => id !== moduleId);
      // If removing Time Table, also remove Teachers Planner
      if (moduleId === 3 && selectedModules.includes(5)) {
        newSelection = newSelection.filter((id) => id !== 5);
      }
      setSelectedModules(newSelection);
    } else {
      let newSelection = [...selectedModules, moduleId];
      // If adding Teachers Planner, auto-add Time Table if missing
      if (moduleId === 5 && !selectedModules.includes(3)) {
        newSelection = [...newSelection, 3];
      }
      setSelectedModules(newSelection);
    }
  };

  // Coupon handlers for Complete Plan
  const handleCompleteApplyCoupon = async () => {
    if (!completeCouponCode.trim())
      return alert("Please enter a coupon code.");
    if (!selectedCompletePlan) return alert("Please select a plan first.");
    if (completeCouponCode.trim().toUpperCase() === "WELCOME20") {
      if (!userData?.created) {
        return alert("Your coupon code has expired.");
      }
      const hoursSinceCreation = (new Date() - new Date(userData.created)) / (1000 * 60 * 60);
      if (hoursSinceCreation > 24) {
        return alert("Your coupon code has expired. It was valid only within 24 hours of registration.");
      }
    }
    setIsValidatingCoupon(true);
    try {
      const res = await fetch(
        "https://web1024.docmaster.in:5010/api/coupons/validate",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-tenant-db": "main_admin_db",
          },
          body: JSON.stringify({
            code: completeCouponCode,
            plan_types: [String(selectedCompletePlan.model_id)],
            school_id: userData?.admin_id,
          }),
        },
      );
      const data = await res.json();
      if (!data.success) return alert(data.message || "Invalid coupon");
      setCompleteCouponId(data.id);
      setCompleteAppliedCoupon({
        code: completeCouponCode,
        discount: data.discount || 0,
      });
    } catch (error) {
      alert("Error validating coupon");
    } finally {
      setIsValidatingCoupon(false);
    }
  };

  const handleCompleteRemoveCoupon = () => {
    setCompleteCouponCode("");
    setCompleteAppliedCoupon(null);
    setCompleteCouponId(null);
  };

  // Coupon handlers for Customized Plan
  const handleCustomApplyCoupon = async () => {
    if (!customCouponCode.trim())
      return alert("Please enter a coupon code.");
    if (!selectedCustomPlan) return alert("Please select a plan first.");
    if (customCouponCode.trim().toUpperCase() === "WELCOME20") {
      if (!userData?.created) {
        return alert("Your coupon code has expired.");
      }
      const hoursSinceCreation = (new Date() - new Date(userData.created)) / (1000 * 60 * 60);
      if (hoursSinceCreation > 24) {
        return alert("Your coupon code has expired. It was valid only within 24 hours of registration.");
      }
    }
    setIsValidatingCoupon(true);
    try {
      const res = await fetch(
        "https://web1024.docmaster.in:5010/api/coupons/validate",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-tenant-db": "main_admin_db",
          },
          body: JSON.stringify({
            code: customCouponCode,
            plan_types: [String(selectedCustomPlan.model_id)],
            school_id: userData?.admin_id,
          }),
        },
      );
      const data = await res.json();
      if (!data.success) return alert(data.message || "Invalid coupon");
      setCustomCouponId(data.id);
      setCustomAppliedCoupon({
        code: customCouponCode,
        discount: data.discount || 0,
      });
    } catch (error) {
      alert("Error validating coupon");
    } finally {
      setIsValidatingCoupon(false);
    }
  };

  const handleCustomRemoveCoupon = () => {
    setCustomCouponCode("");
    setCustomAppliedCoupon(null);
    setCustomCouponId(null);
  };

  // Create coupon link
  const createCouponLink = async (couponId) => {
    if (!couponId) return null;
    const schoolId = userData?.admin_id;
    try {
      const response = await fetch(
        "https://web1024.docmaster.in:5010/api/coupons/createCouponLink",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-tenant-db": "main_admin_db",
          },
          body: JSON.stringify({
            schoolId,
            couponId,
            createdById: schoolId,
            status: "incompleted",
          }),
        },
      );
      if (response.ok) {
        const data = await response.json();
        return data.insertId;
      }
      return null;
    } catch (error) {
      console.error("Error creating coupon link:", error);
      return null;
    }
  };

  // Payment handler for Complete Plan
  const handleCompletePayment = async () => {
    if (!user) return alert("Please login to continue.");
    if (!selectedCompletePlan) return alert("Please select a plan.");

    const linkData = await createCouponLink(completeCouponId);

    // const body = {
    //   txnRefNo: Date.now(),
    //   amount: completePlanFinalPrice,
    //   txnType: "Pay",
    //   orderInfo: String(selectedCompletePlan.model_id),
    //   email: user.email,
    //   firstName: "sample",
    //   lastName: "sample",
    //   street: "Delhi",
    //   city: "",
    //   state: "",
    //   zip: 506002,
    //   phone: user?.contact_number || 0,
    //   UDF01: user.admin_id,
    //   UDF02: user?.id,
    //   UDF03: [],
    //   UDF04: "complete",
    //   UDF05: 1,
    //   UDF06: linkData || "",
    // };

    const body = {
      txnRefNo: Date.now(),
      amount: completePlanFinalPrice,
      // amount: 1,
      txnType: "Pay",
      orderInfo: String(selectedCompletePlan.model_id),
      email: user.email,
      firstName: "sample",
      lastName: "sample",
      street: "Delhi",
      city: "",
      state: "",
      zip: 506002,
      phone: user?.contact_number || 0,
      udf21: user.admin_id,
      udf22: user?.id,
      udf23: [],
      udf24: "complete",
      udf25: 1,
      udf26: linkData || "",
      udf27: String(selectedCompletePlan.model_id),
    };
    console.log("Complete Plan Payment Body:", body);
    dispatch(InitiatePaymentThunk(body));
  };

  // Payment handler for Customized Plan
  const handleCustomizedPayment = async () => {
    if (!user) return alert("Please login to continue.");
    if (!isCustomizedValid) return alert("Please complete your selection.");

    // Check if Teachers Planner is selected without Time Table
    if (selectedModules.includes(5) && !selectedModules.includes(3)) {
      return alert("Please select Time Table to use Teacher's Planner.");
    }

    const linkData = await createCouponLink(customCouponId);

    // const body = {
    //   txnRefNo: Date.now(),
    //   amount: customizedFinalPrice,
    //   txnType: "Pay",
    //   orderInfo: String(selectedCustomPlan.model_id),
    //   email: user.email,
    //   firstName: "sample",
    //   lastName: "sample",
    //   street: "Delhi",
    //   city: "",
    //   state: "",
    //   zip: 506002,
    //   phone: user?.contact_number || 0,
    //   UDF01: user.admin_id,
    //   UDF02: user?.id,
    //   UDF03: selectedModules.join(","),
    //   UDF04: "customized",
    //   UDF05: numberOfUsers,
    //   UDF06: linkData || "",
    // };

    

    const body = {
      txnRefNo: Date.now(),
      amount: customizedFinalPrice,
      //amount: 1,
      txnType: "Pay",
      orderInfo: String(selectedCustomPlan.model_id),
      email: user.email,
      firstName: "sample",
      lastName: "sample",
      street: "Delhi",
      city: "",
      state: "",
      zip: 506002,
      phone: user?.contact_number || 0,
      udf21: user.admin_id,
      udf22: user?.id,
      udf23: selectedModules.join(","),
      udf24: "customized",
      udf25: numberOfUsers,
      udf26: linkData || "",
      udf27: String(selectedCustomPlan.model_id),
    };
    console.log("Customized Plan Payment Body:", body);
    dispatch(InitiatePaymentThunk(body));
  };

  // Show loader while fetching plans
  if (loader) return <SkeletonLoader type={1} />;
  if (error) {
    alert(error);
    return null;
  }

  return (
    <>
      {/* Navbar */}
      <nav
        className="navbar navbar-expand-lg p-0"
        style={{
          background: "linear-gradient(135deg, #0b3a83, #1e5799, #3a8fd9)",
          fontSize: "0.85rem",
          boxShadow: "0 2px 12px rgba(11, 58, 131, 0.3)",
        }}
      >
        <div
          className="container-fluid px-0 mx-auto"
          style={{ maxWidth: "100%" }}
        >
          {/* Home icon - visible on mobile instead of hamburger */}
          <Link to="/dashboard" className="nav-link text-white d-lg-none ms-3" style={{ fontSize: "1.2rem" }}>
            <i className="fa fa-home"></i>
          </Link>
          <div className="collapse navbar-collapse" id="navbarNav">
            <ul className="navbar-nav ps-4 w-100 d-flex gap-3 justify-content-start">
              <li
                className={`nav-item ${["/dashboard"].includes(pathname) ? "active" : ""}`}
              >
                <Link to="/dashboard" className="nav-link text-white">
                  <i
                    className="fa fa-home"
                    style={{ pointerEvents: "none" }}
                  ></i>{" "}
                  Home
                </Link>
              </li>
            </ul>
          </div>
          <div className="d-flex align-items-center justify-content-end gap-3">
            <div className="dropdown me-3">
              <button
                className="btn btn-light dropdown-toggle d-flex align-items-center"
                type="button"
                id="userDropdown"
                data-bs-toggle="dropdown"
                aria-expanded="false"
                style={{ borderRadius: "25px", fontWeight: "600" }}
              >
                <i className="fa fa-user me-2"></i>
                {userData.name}
              </button>
              <ul
                className="dropdown-menu dropdown-menu-end"
                aria-labelledby="userDropdown"
              >
                <li>
                  <span className="dropdown-item">Profile</span>
                </li>
                <li>
                  <span className="dropdown-item" onClick={logOut}>
                    Log Out
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Tab Switcher — only visible on mobile */}
      <div className="pricing-mobile-tabs">
        <button
          className={`pricing-tab-btn${mobileTab === "individual" ? " active individual-active" : ""}`}
          onClick={() => setMobileTab("individual")}
        >
          Plan for Individual
        </button>
        <button
          className={`pricing-tab-btn${mobileTab === "school" ? " active school-active" : ""}`}
          onClick={() => setMobileTab("school")}
        >
          Complete Module for Schools
        </button>
      </div>

      {/* Main Content */}
      <div className="pricing-main-content">
        <div className="pricing-container">
          {/* Header */}
          {/* <div className="pricing-header">
            <h1>
              Choose Your Plan
            </h1>
            <p>
              All plans include 12 months of access.
            </p>
          </div> */}

          {/* Two Columns Layout */}
          <div className="pricing-columns">
            {/* Column 2: Customized Plan for Individual Users */}
            <div className={`pricing-column individual-column${mobileTab !== "individual" ? " mobile-hidden" : ""}`}>
              <div className="column-header">
                <div className="header-content-wrapper">
                  <div className="header-text-section">
                    <h2>Plan for Individuals</h2>
                    <p className="column-subtitle">Build your own bundle</p>
                  </div>
                  <div className="header-price-section individual">
                    <div className="header-price-label">Starts From</div>
                    <div className="header-price-values">
                      <span className="header-price-current individual">
                        <h6>₹250/user</h6>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="school-features-header">
                <span>Choose Your Features</span>
                <span className="badge-year">For 1 year</span>
              </div>


              {/* Vertical Landing-page style module cards — Documents always pre-checked */}
              <div className="ind-vertical-cards">
                {allModules.map((module) => {
                  const isSelected = selectedModules.includes(module.id);
                  const price = module.id === 6 ? "₹0" : "₹250";
                  return (
                    <div
                      key={module.id}
                      className={`ind-horiz-card ${isSelected ? "selected" : ""} ${module.locked ? "locked" : ""}`}
                      style={{ background: module.gradient }}
                      onClick={() => handleModuleToggle(module.id)}
                    >
                      {/* Icon box */}
                      <div className="ind-horiz-icon">
                        <img src={module.icon} alt={module.name} onError={(e) => (e.currentTarget.style.display = "none")} />
                      </div>
                      {/* Text */}
                      <div className="ind-horiz-text">
                        <span className="ind-horiz-name">{module.name}</span>
                        <span className="ind-horiz-subname">{module.subName}</span>
                      </div>
                      {/* Price + badge */}
                      <div className="ind-horiz-right">
                        <span className="ind-horiz-price">{price}<span className="ind-horiz-per">/user</span></span>
                        <span className={`school-module-badge badge-${module.badgeVariant}`}>{module.badge}</span>
                      </div>
                      {/* Checkbox circle */}
                      <div className={`ind-horiz-check ${isSelected ? "checked" : ""}`}>
                        {isSelected && "✓"}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Summary + payment — shown once at least 1 non-doc module selected */}
              {selectedCustomPlan && (
                <div className="ind-summary">
                  {/* Number of Users */}
                  <div className="users-section">
                    <label>Number of Users:</label>
                    <div className="users-input">
                      <button onClick={() => setNumberOfUsers(Math.max(1, numberOfUsers - 1))} disabled={customAppliedCoupon !== null}>-</button>
                      <input
                        type="number"
                        value={numberOfUsers}
                        onChange={(e) => setNumberOfUsers(Math.max(1, parseInt(e.target.value) || 1))}
                        min="1"
                        disabled={customAppliedCoupon !== null}
                      />
                      <button onClick={() => setNumberOfUsers(numberOfUsers + 1)} disabled={customAppliedCoupon !== null}>+</button>
                    </div>
                  </div>

                  {/* Coupon */}
                  <div className="coupon-row">
                    <input
                      type="text"
                      placeholder="Enter coupon code"
                      value={customCouponCode}
                      onChange={(e) => setCustomCouponCode(e.target.value)}
                      disabled={customAppliedCoupon !== null}
                    />
                    {customAppliedCoupon ? (
                      <button className="coupon-btn remove" onClick={handleCustomRemoveCoupon}>Remove</button>
                    ) : (
                      <button className="coupon-btn apply" onClick={handleCustomApplyCoupon} disabled={isValidatingCoupon}>
                        {isValidatingCoupon ? "..." : "Apply"}
                      </button>
                    )}
                  </div>
                  {customAppliedCoupon && (
                    <div className="coupon-success">
                      Coupon "{customAppliedCoupon.code}" applied ({typeof customAppliedCoupon.discount === "string" ? customAppliedCoupon.discount : `₹${customAppliedCoupon.discount}`} off)
                    </div>
                  )}

                  {/* Price Breakdown */}
                  <div className="price-breakdown">
                    <div className="breakdown-row">
                      <span>Price per user</span>
                      <span>₹{customizedBasePricePerUser}</span>
                    </div>
                    <div className="breakdown-row">
                      <span>Number of users</span>
                      <span>× {numberOfUsers}</span>
                    </div>
                    {customTier?.discountPct > 0 && (
                      <div className="breakdown-row">
                        <span>Discount</span>
                        <span style={{ color: "#0f9b47" }}>{customTier.discountPct}%</span>
                      </div>
                    )}
                    <div className="breakdown-row subtotal">
                      <span>Subtotal</span>
                      <span>₹{customizedPriceAfterDiscount.toLocaleString()}</span>
                    </div>
                  </div>

                  <div className="total-row">
                    <span>Total Amount:</span>
                    <span className="total-price">₹{customizedFinalPrice.toLocaleString()}</span>
                  </div>

                  <button
                    className={`pay-button individual ${!isCustomizedValid ? "disabled" : ""}`}
                    onClick={handleCustomizedPayment}
                    disabled={!isCustomizedValid}
                  >
                    Pay Now ₹{customizedFinalPrice.toLocaleString()}
                  </button>
                </div>
              )}
            </div>
            {/* Column 1: Complete Modules for School */}
            <div className={`pricing-column school-column${mobileTab !== "school" ? " mobile-hidden" : ""}`}>
              <div className="column-header">
                <div className="header-content-wrapper">
                  <div className="header-text-section">
                    <h2>Complete Module for Schools</h2>
                    {/* <p className="column-subtitle">Build your own bundle</p> */}
                  </div>
                  <div className="header-price-section">
                    <div className="header-price-label">Starts From</div>
                    <div className="header-price-values">
                      <span className="header-price-current"><h6>₹5000/user</h6></span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Module Features - always visible */}
              <div className="school-features-header">
                <span>All Features Included In School Modules</span>
                <span className="badge-year">For 1 year</span>
              </div>
              <div className="school-modules-grid">
                {schoolModulesList.map((module, idx) => {
                  const badge = module.dynamic
                    ? `${qpCountByUsers[selectedCompletePlan?.total_users] || 50} PAPERS`
                    : module.badge;
                  return (
                    <div key={idx} className="school-module-card" style={{ backgroundColor: module.color }}>
                      <div className="school-module-icon-box">
                        <img src={module.icon} alt={module.name} className="school-module-icon" onError={(e) => (e.currentTarget.style.display = "none")} />
                      </div>
                      <div className="school-module-text">
                        <span className="school-module-name">{module.name}</span>
                        <span className="school-module-subname">{module.subName}</span>
                      </div>
                      <span className={`school-module-badge badge-${module.badgeVariant || "unlimited"}`}>{badge}</span>
                    </div>
                  );
                })}
              </div>

              {/* User Count Selection */}
              <div className="choose-users-label">Choose Your Plan:</div>
              <div className="users-count-row">
                {completePlans.map((plan) => (
                  <div
                    key={plan.model_id}
                    className={`user-count-card ${selectedCompletePlan?.model_id === plan.model_id ? "selected" : ""}`}
                    onClick={() => {
                      if (selectedCompletePlan?.model_id === plan.model_id) {
                        setSelectedCompletePlan(null);
                      } else {
                        setSelectedCompletePlan(plan);
                        setSelectedModules([6]);
                        setCustomCouponCode("");
                        setCustomAppliedCoupon(null);
                        setCustomCouponId(null);
                      }
                    }}
                  >
                    <div className="user-count-number">{plan.total_users} users</div>
                    <div className="user-count-subtext">(1 Admin + {plan.total_users - 1} users)</div>
                    <div className="user-count-price">₹{Number(plan.price).toLocaleString()} /-</div>
                  </div>
                ))}
              </div>

              {/* Coupon & Payment - shown when plan selected */}
              {selectedCompletePlan && (
                <div className="school-footer">
                  <div className="coupon-row">
                    <input
                      type="text"
                      placeholder="Enter coupon code"
                      value={completeCouponCode}
                      onChange={(e) => setCompleteCouponCode(e.target.value)}
                      disabled={completeAppliedCoupon !== null}
                    />
                    {completeAppliedCoupon ? (
                      <button className="coupon-btn remove" onClick={handleCompleteRemoveCoupon}>Remove</button>
                    ) : (
                      <button className="coupon-btn apply" onClick={handleCompleteApplyCoupon} disabled={isValidatingCoupon}>
                        {isValidatingCoupon ? "..." : "Apply"}
                      </button>
                    )}
                  </div>
                  {completeAppliedCoupon && (
                    <div className="coupon-success">
                      Coupon "{completeAppliedCoupon.code}" applied (
                      {typeof completeAppliedCoupon.discount === "string"
                        ? completeAppliedCoupon.discount
                        : `₹${completeAppliedCoupon.discount}`}{" "}
                      off)
                    </div>
                  )}
                  <div className="total-row">
                    <span>Total Amount:</span>
                    <span className="total-price">₹{completePlanFinalPrice.toLocaleString()}</span>
                  </div>
                  <button className="pay-button school" onClick={handleCompletePayment}>
                    PAY NOW
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}

export default PricingPage;